import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { RotateCcw, Trash2, Loader2, FileText, Mail, Briefcase } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const ICONS = { resume: FileText, letter: Mail, job: Briefcase };

export default function TrashPanel() {
  const confirm = useConfirm();
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [retentionDays, setRetentionDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [emptyBusy, setEmptyBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trash");
      setItems(data.items || []);
      setRetentionDays(data.retention_days || 30);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dashLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const key = (it) => `${it.kind}:${it.id}`;

  const restore = async (it) => {
    setBusyId(key(it));
    try {
      await api.post(`/trash/${it.kind}/${it.id}/restore`);
      setItems((prev) => prev.filter((x) => key(x) !== key(it)));
      toast.success(t("trashRestored"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dashDeleteFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const hardDelete = async (it) => {
    if (!(await confirm(t("trashConfirmHard")))) return;
    setBusyId(key(it));
    try {
      await api.delete(`/trash/${it.kind}/${it.id}`);
      setItems((prev) => prev.filter((x) => key(x) !== key(it)));
      toast.success(t("trashHardDeleted"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dashDeleteFailed"));
    } finally {
      setBusyId(null);
    }
  };

  const emptyAll = async () => {
    if (!(await confirm(t("trashConfirmEmpty", { count: items.length })))) return;
    setEmptyBusy(true);
    try {
      const { data } = await api.delete(`/trash`);
      setItems([]);
      toast.success(t("trashEmptied", { count: data.removed }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dashDeleteFailed"));
    } finally {
      setEmptyBusy(false);
    }
  };

  return (
    <div data-testid="trash-panel" className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500 inline-flex items-center gap-2">
            <Trash2 size={12} strokeWidth={1.5} /> {t("navTrash")}
          </p>
          <h2 className="font-serif text-3xl mt-1">{t("trashTitle")}</h2>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            {t("trashLead", { days: retentionDays })}
          </p>
        </div>
        {items.length > 0 && (
          <button
            data-testid="trash-empty-all"
            onClick={emptyAll}
            disabled={emptyBusy}
            className="border border-neutral-300 text-rose-700 hover:border-rose-700 px-4 py-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] inline-flex items-center gap-2"
          >
            {emptyBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} strokeWidth={1.5} />}
            {t("trashEmptyAll")}
          </button>
        )}
      </header>

      {loading ? (
        <div className="text-neutral-500 text-sm inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t("loading")}
        </div>
      ) : items.length === 0 ? (
        <div data-testid="trash-empty" className="border border-dashed border-neutral-300 p-10 text-center">
          <Trash2 size={32} strokeWidth={1.2} className="text-neutral-400 mx-auto" />
          <p className="mt-4 font-serif text-xl">{t("trashEmpty")}</p>
          <p className="text-sm text-neutral-500 mt-1">{t("trashEmptyHint")}</p>
        </div>
      ) : (
        <div className="border border-neutral-300">
          <div className="grid grid-cols-[24px_1fr_120px_120px_140px] gap-4 px-4 py-3 border-b border-neutral-300 bg-neutral-100 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-600">
            <span />
            <span>{t("trashColLabel")}</span>
            <span>{t("trashColKind")}</span>
            <span>{t("trashColDaysLeft")}</span>
            <span className="text-right">{t("trashColActions")}</span>
          </div>
          {items.map((it, idx) => {
            const Icon = ICONS[it.kind] || FileText;
            const busy = busyId === key(it);
            const kindLabel = t(`trashKind_${it.kind}`);
            return (
              <div
                key={key(it)}
                data-testid={`trash-row-${idx}`}
                className="grid grid-cols-[24px_1fr_120px_120px_140px] gap-4 px-4 py-3 border-b border-neutral-200 last:border-b-0 items-center hover:bg-neutral-50"
              >
                <Icon size={14} strokeWidth={1.6} className="text-neutral-500" />
                <span className="font-serif truncate">{it.label}</span>
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.1em] text-neutral-600">{kindLabel}</span>
                <span className={`font-mono text-xs ${it.days_left <= 3 ? "text-rose-700" : "text-neutral-700"}`}>
                  {t("trashDaysLeft", { days: it.days_left })}
                </span>
                <div className="flex justify-end gap-2">
                  <button
                    data-testid={`trash-restore-${idx}`}
                    onClick={() => restore(it)}
                    disabled={busy}
                    title={t("trashRestore")}
                    className="p-2 border border-neutral-300 hover:border-emerald-700 hover:text-emerald-700 transition-colors"
                  >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} strokeWidth={1.7} />}
                  </button>
                  <button
                    data-testid={`trash-hard-delete-${idx}`}
                    onClick={() => hardDelete(it)}
                    disabled={busy}
                    title={t("trashHardDelete")}
                    className="p-2 border border-neutral-300 text-neutral-500 hover:border-rose-700 hover:text-rose-700 transition-colors"
                  >
                    <Trash2 size={12} strokeWidth={1.7} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
